import type { CategoryDef, Reminder } from '../lib/api'

/* Categories are user-owned rows, so nothing here may key off a category slug.
 * A category picks a *colour key* and an *icon key*; this module owns both
 * vocabularies and the fallback for values it doesn't recognise. */

export type ColorKey =
  | 'green'
  | 'pink'
  | 'blue'
  | 'purple'
  | 'teal'
  | 'amber'
  | 'red'
  | 'slate'

export const COLOR_KEYS: ColorKey[] = [
  'green',
  'pink',
  'blue',
  'purple',
  'teal',
  'amber',
  'red',
  'slate',
]

export type IconKey =
  | 'cart'
  | 'bag'
  | 'tag'
  | 'dumbbell'
  | 'pill'
  | 'home'
  | 'car'
  | 'gift'
  | 'book'
  | 'coffee'
  | 'tools'
  | 'paw'

export const ICON_KEYS: IconKey[] = [
  'cart',
  'bag',
  'tag',
  'dumbbell',
  'pill',
  'home',
  'car',
  'gift',
  'book',
  'coffee',
  'tools',
  'paw',
]

const FALLBACK_COLOR = 'var(--ink-3)'
const FALLBACK_ICON: IconKey = 'tag'

/** A colour key resolved to the token it names. Unknown keys stay neutral rather
 * than emitting `var(--cat-undefined)`, which would render as black. */
export function colorVar(key: string): string {
  return (COLOR_KEYS as string[]).includes(key) ? `var(--cat-${key})` : FALLBACK_COLOR
}

/** Everything the UI needs to draw a category. */
export interface CategoryLook {
  label: string
  color: string
  icon: IconKey
  /** False when the slug resolved to nothing — the Uncategorized look. */
  known: boolean
}

export const UNCATEGORIZED: CategoryLook = {
  label: 'Uncategorized',
  color: FALLBACK_COLOR,
  icon: FALLBACK_ICON,
  known: false,
}

function look(def: CategoryDef): CategoryLook {
  return {
    label: def.label,
    color: colorVar(def.color),
    icon: (ICON_KEYS as string[]).includes(def.icon) ? (def.icon as IconKey) : FALLBACK_ICON,
    known: true,
  }
}

/** Resolve a slug against the signed-in user's categories. */
export function resolveCategory(slug: string, categories: CategoryDef[]): CategoryLook {
  const def = categories.find((c) => c.slug === slug)
  return def ? look(def) : UNCATEGORIZED
}

/** How a reminder should be drawn.
 *
 * The viewer's own categories win, so renaming or recolouring repaints the board
 * immediately. `category_detail` is the fallback, and it exists so a household
 * member's reminder still renders properly under a category the viewer doesn't
 * own. Only when both miss does the reminder read as Uncategorized. */
export function reminderLook(
  reminder: Pick<Reminder, 'category' | 'category_detail'>,
  categories: CategoryDef[],
): CategoryLook {
  const mine = categories.find((c) => c.slug === reminder.category)
  if (mine) return look(mine)
  if (reminder.category_detail) return look(reminder.category_detail)
  return UNCATEGORIZED
}

/** Line-art glyphs — 24x24, stroke only, so they inherit the category colour. */
const PATHS: Record<IconKey, React.ReactNode> = {
  cart: (
    <>
      <path d="M3 5h2l2.2 9.5a2 2 0 0 0 2 1.5h7.2a2 2 0 0 0 2-1.6L20 8H6" />
      <circle cx="10" cy="20" r="1.3" />
      <circle cx="17" cy="20" r="1.3" />
    </>
  ),
  bag: (
    <>
      <path d="M5 8h14l-1.2 12.2a1.6 1.6 0 0 1-1.6 1.4H7.8a1.6 1.6 0 0 1-1.6-1.4Z" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    </>
  ),
  tag: (
    <>
      <path d="M20.6 12.9 12.9 20.6a2 2 0 0 1-2.8 0l-6.7-6.7a2 2 0 0 1-.6-1.6l.5-6a2 2 0 0 1 1.8-1.8l6-.5a2 2 0 0 1 1.6.6l6.7 6.7a2 2 0 0 1 0 2.8Z" />
      <circle cx="8.3" cy="8.3" r="1.4" />
    </>
  ),
  dumbbell: (
    <>
      <path d="M4 9v6M7 7v10M17 7v10M20 9v6M7 12h10" />
    </>
  ),
  pill: (
    <>
      <path d="M14.5 3.5a5 5 0 0 1 7 7l-8 8a5 5 0 0 1-7-7Z" />
      <path d="M9 9l6 6" />
    </>
  ),
  home: (
    <>
      <path d="M3.6 10.6 12 3.9l8.4 6.7" />
      <path d="M5.8 9.2v10.4a1 1 0 0 0 1 1h10.4a1 1 0 0 0 1-1V9.2" />
      <path d="M9.8 20.6v-6.2h4.4v6.2" />
    </>
  ),
  car: (
    <>
      <path d="M5.6 13.4 7.2 8.7a2 2 0 0 1 1.9-1.4h5.8a2 2 0 0 1 1.9 1.4l1.6 4.7" />
      <path d="M3.6 13.4h16.8V17a1 1 0 0 1-1 1H4.6a1 1 0 0 1-1-1Z" />
      <circle cx="7.4" cy="18" r="1.4" />
      <circle cx="16.6" cy="18" r="1.4" />
    </>
  ),
  gift: (
    <>
      <path d="M12 11.6c0-2.6-1.3-4.6-3.1-4.6a2.1 2.1 0 0 0 0 4.2h6.2a2.1 2.1 0 0 0 0-4.2c-1.8 0-3.1 2-3.1 4.6Z" />
      <path d="M3.8 11.6h16.4v2.8H3.8z" />
      <path d="M5.4 14.4v5.2a1 1 0 0 0 1 1h11.2a1 1 0 0 0 1-1v-5.2" />
      <path d="M12 11.6v9" />
    </>
  ),
  book: (
    <>
      <path d="M12 7.2C10.4 5.6 8 5 4.6 5.2a.9.9 0 0 0-.8.9v10.6a.9.9 0 0 0 .9.9c3.2-.2 5.6.4 7.3 1.9" />
      <path d="M12 7.2c1.6-1.6 4-2.2 7.3-2a.9.9 0 0 1 .9.9v10.6a.9.9 0 0 1-.8.9c-3.4-.2-5.8.4-7.4 1.9" />
      <path d="M12 7.2v12.3" />
    </>
  ),
  coffee: (
    <>
      <path d="M4.4 8.8h12.2V15a4.4 4.4 0 0 1-4.4 4.4H8.8A4.4 4.4 0 0 1 4.4 15Z" />
      <path d="M16.6 10.4h1.6a2.6 2.6 0 0 1 0 5.2h-1.6" />
      <path d="M8.2 3.4v2.2M12.4 3.4v2.2" />
    </>
  ),
  tools: (
    <>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </>
  ),
  paw: (
    <>
      <circle cx="9" cy="6.6" r="1.9" />
      <circle cx="14.6" cy="6.6" r="1.9" />
      <circle cx="5.2" cy="10.8" r="1.7" />
      <circle cx="18.4" cy="10.8" r="1.7" />
      <path d="M11.8 12.6c2 0 3.2 1.4 4 2.9.7 1.3 1.7 2.2 1.7 3.6 0 1.6-1.3 2.6-2.9 2.6-1.2 0-1.7-.5-2.8-.5s-1.6.5-2.8.5c-1.6 0-2.9-1-2.9-2.6 0-1.4 1-2.3 1.7-3.6.8-1.5 2-2.9 4-2.9Z" />
    </>
  ),
}

/** Draws one glyph. Pass a resolved CSS colour, or omit it to inherit. */
export function CategoryIcon({
  icon,
  color,
  size = 26,
}: {
  icon: string
  color?: string
  size?: number
}) {
  const key = (ICON_KEYS as string[]).includes(icon) ? (icon as IconKey) : FALLBACK_ICON
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? 'currentColor'}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[key]}
    </svg>
  )
}
